import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Eye, EyeOff, GripVertical, ImageOff, Trash2 } from "lucide-react";
import type { StorefrontItem } from "@lucralink/shared";
import { Button } from "@/components/ui/button";
import { marketplaceLabel, formatPriceCents } from "@/lib/marketplace";
import { cn } from "@/lib/utils";

interface StorefrontItemRowProps {
  item: StorefrontItem;
  onToggleVisible: (item: StorefrontItem) => void;
  onRemove: (item: StorefrontItem) => void;
  isBusy?: boolean;
}

/**
 * Linha arrastavel de item da vitrine. O handle (GripVertical) recebe os
 * listeners do dnd-kit; o resto da linha continua clicavel normalmente.
 */
export function StorefrontItemRow({
  item,
  onToggleVisible,
  onRemove,
  isBusy = false,
}: StorefrontItemRowProps): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const priceLabel = formatPriceCents(item.priceCents);

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`storefront-item-${item.id}`}
      className={cn(
        "flex items-center gap-3 rounded-lg border border-border bg-[hsl(var(--surface)/0.75)] px-3 py-2.5",
        isDragging && "opacity-60 shadow-md",
        !item.isVisible && "opacity-70",
      )}
    >
      <button
        type="button"
        className="flex h-8 w-7 shrink-0 cursor-grab touch-none items-center justify-center rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:cursor-grabbing"
        aria-label={`Reordenar ${item.title}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical size={16} />
      </button>

      <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-background/60">
        {item.imageUrl ? (
          <img
            src={item.imageUrl}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
            width={48}
            height={48}
          />
        ) : (
          <ImageOff size={16} className="text-muted-foreground/60" aria-hidden="true" />
        )}
      </div>

      <div className="min-w-0 flex-1 space-y-1">
        <p className="truncate text-sm font-medium text-foreground">{item.title}</p>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="inline-flex items-center rounded-md border border-border bg-[hsl(var(--surface-raised))] px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
            {marketplaceLabel(item.marketplaceCode)}
          </span>
          {priceLabel ? (
            <span className="text-xs font-semibold text-foreground">{priceLabel}</span>
          ) : (
            <span className="text-xs text-muted-foreground">sem preço</span>
          )}
          {!item.isVisible && (
            <span className="inline-flex items-center rounded-md border border-border/80 bg-background/40 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
              oculto
            </span>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          onClick={() => onToggleVisible(item)}
          disabled={isBusy}
          aria-label={item.isVisible ? "Ocultar item" : "Mostrar item"}
          aria-pressed={item.isVisible}
        >
          {item.isVisible ? <Eye size={15} /> : <EyeOff size={15} />}
        </Button>
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          onClick={() => onRemove(item)}
          disabled={isBusy}
          aria-label="Remover item"
          className="hover:text-destructive"
        >
          <Trash2 size={15} />
        </Button>
      </div>
    </div>
  );
}
